import type {Feature} from "../feature";
import type {GraphAtom} from "../graph/GraphAtom";
import type {MolecularGraph} from "../graph/MolecularGraph";
import {classifyFeature} from "../serialization/classifyFeature";

/**
 * PIPH-318: molecule-scoped enumeration for UNCLAIMED atoms.
 *
 * An atom that belongs to no real feature is modeled as a singleton "atom"-type
 * feature (one heavy atom). Per-feature numbering then scopes to that one atom,
 * so `nameAtom` never adds a "-N" suffix and five separate chlorines all read
 * "Chlorine" — indistinguishable in the "Other atoms" list.
 *
 * This indexer counts same-element unclaimed atoms across each connected
 * component (molecule) and hands back a 1-based index + per-molecule total for
 * each, so the serializer can call `nameAtom(symbol, …, elementIndex, total)`
 * and get "Chlorine-1".."Chlorine-5". `nameAtom`'s `total > 1` gate keeps a lone
 * unclaimed atom bare ("Bromine"), so numbering only appears when there's actual
 * ambiguity.
 *
 * Only single-atom "atom" features are enumerated (the PIPH-318 card). Multi-atom
 * unclaimed clusters are out of scope.
 */
export interface MoleculeElementIndex {
    /** 1-based position among same-element unclaimed atoms in this molecule. */
    elementIndex: number;
    /** Total same-element unclaimed atoms in this molecule. */
    totalOfElement: number;
}

/**
 * Map a GraphAtom → its connected-component (molecule) id, preserving GraphAtom
 * identity (unlike `graph.getMolecules()`, which rebuilds subset graphs and
 * reassigns atom indices). A simple BFS over the graph's own adjacency.
 */
function componentIdByAtom(graph: MolecularGraph): Map<GraphAtom, number> {
    const componentId = new Map<GraphAtom, number>();
    let nextId = 0;
    for (const atom of graph.atoms) {
        if (componentId.has(atom)) continue;
        const id = nextId++;
        const queue: GraphAtom[] = [atom];
        componentId.set(atom, id);
        while (queue.length > 0) {
            const curr = queue.shift()!;
            for (const bond of graph.getBondsAttachedTo(curr)) {
                const other = bond.getOtherAtom(curr);
                if (!componentId.has(other)) {
                    componentId.set(other, id);
                    queue.push(other);
                }
            }
        }
    }
    return componentId;
}

/**
 * Compute per-molecule, per-element index/total for unclaimed atoms (those that
 * are the sole heavy atom of a singleton "atom"-type feature). Keyed by
 * `GraphAtom.index` (the top-level graph index, which is also `SerializedAtom.index`).
 */
export function computeMoleculeElementIndices(
    features: Feature[],
    graph: MolecularGraph,
): Map<number, MoleculeElementIndex> {
    // The heavy atom of each singleton "atom" feature. Sorted by graph index so
    // enumeration follows the molecule's atom order (Chlorine-1 = first Cl in the
    // config), independent of feature-iteration order.
    const unclaimedHeavyAtoms: GraphAtom[] = [];
    for (const f of features) {
        if (classifyFeature(f) !== "atom") continue;
        const heavy = f.atoms.filter(a => a.symbol !== "H");
        // Single-atom unclaimed only (PIPH-318 scope).
        if (heavy.length !== 1) continue;
        unclaimedHeavyAtoms.push(heavy[0]);
    }
    if (unclaimedHeavyAtoms.length === 0) return new Map();
    unclaimedHeavyAtoms.sort((a, b) => a.index - b.index);

    const componentId = componentIdByAtom(graph);

    // First pass: per-(molecule, symbol) totals.
    const totals = new Map<string, number>();
    const key = (comp: number, symbol: string) => `${comp} ${symbol}`;
    for (const atom of unclaimedHeavyAtoms) {
        const comp = componentId.get(atom) ?? -1;
        const k = key(comp, atom.symbol);
        totals.set(k, (totals.get(k) ?? 0) + 1);
    }

    // Second pass: running 1-based index in graph order, stable per molecule.
    const running = new Map<string, number>();
    const result = new Map<number, MoleculeElementIndex>();
    for (const atom of unclaimedHeavyAtoms) {
        const comp = componentId.get(atom) ?? -1;
        const k = key(comp, atom.symbol);
        const next = (running.get(k) ?? 0) + 1;
        running.set(k, next);
        result.set(atom.index, {
            elementIndex: next,
            totalOfElement: totals.get(k) ?? 1,
        });
    }
    return result;
}
